import React, { useCallback, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { adminApi } from '@/utils/api';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNotify } from '@/context/NotificationContext';

/**
 * Admin-managed media (image or video) for a top banner slot.
 * The value lives in `system_configuration` under a `bg_*` key, so it is read
 * together with every other tile background via GET /api/home/profile-tiles-bg.
 * Images are converted to WebP and videos are transcoded to a light 720p mp4
 * on the server before they are stored.
 */
export const useBannerMedia = (bgKey: string) => {
    const { user, tileBgs, fetchTileBgs } = useStore();
    const { impactOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    const [busy, setBusy] = useState(false);

    const isAdmin = Boolean(user?.role === 'admin' || String(user?.telegram_id) === '601287447' || (user as any)?.is_admin);
    const url: string = (tileBgs as Record<string, string>)?.[bgKey] || '';

    const save = useCallback(async (value: string) => {
        const res = await adminApi.setProfileTileBgs({ [bgKey]: value });
        const data = await res.json().catch(() => ({}));
        if (!data?.success) throw new Error(data?.error || 'save failed');
        await fetchTileBgs(true);
    }, [bgKey, fetchTileBgs]);

    const upload = useCallback(async (file: File) => {
        if (!isAdmin || !file) return;

        // Guard rails: a stalled multi-megabyte upload on mobile data looks like a
        // dead button, so refuse early with a reason instead of failing silently.
        if (file.size > MAX_UPLOAD_BYTES) {
            showNotification(tc('error'), `${(file.size / 1048576).toFixed(1)} MB is too large — max 20 MB.`, 'error');
            return;
        }
        const dims = await inspectMedia(file);
        if (dims && dims.w > 0 && dims.h > 0) {
            const ratio = dims.w / dims.h;
            if (ratio > 3.5 || ratio < 1 / 3.5) {
                showNotification(tc('error'), `${dims.w}×${dims.h} is too stretched — use ~1200×630, 1080×1080, or a short video.`, 'error');
                return;
            }
        }

        setBusy(true);
        impactOccurred('medium');
        try {
            const res = await adminApi.uploadUIAsset(file);
            const data = await res.json();
            if (data.success && data.url) {
                await save(data.url);
                showNotification(tc('success'), t('mediaUpdated'), 'success');
            } else {
                showNotification(tc('error'), data.error || t('uploadFailed'), 'error');
            }
        } catch (err: any) {
            console.error('[BannerMedia] upload failed', err);
            showNotification(tc('error'), t('mediaUploadFailed'), 'error');
        } finally {
            setBusy(false);
        }
    }, [isAdmin, save, showNotification, t, tc, impactOccurred]);

    const remove = useCallback(async () => {
        if (!isAdmin) return;
        setBusy(true);
        impactOccurred('light');
        try {
            await save('');
            showNotification(tc('success'), t('mediaUpdated'), 'success');
        } catch (err: any) {
            console.error('[BannerMedia] remove failed', err);
            showNotification(tc('error'), t('uploadFailed'), 'error');
        } finally {
            setBusy(false);
        }
    }, [isAdmin, save, showNotification, t, tc, impactOccurred]);

    return { url, isAdmin, busy, upload, remove };
};

const isVideoUrl = (url: string) => /\.(mp4|webm|mov)$/i.test(url);

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Reads the natural size of an image/video without uploading it. */
const inspectMedia = (file: File): Promise<{ w: number; h: number } | null> => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const finish = (v: { w: number; h: number } | null) => { URL.revokeObjectURL(url); resolve(v); };
    if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => finish({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => finish(null);
        img.src = url;
    } else if (file.type.startsWith('video/')) {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.onloadedmetadata = () => finish({ w: v.videoWidth, h: v.videoHeight });
        v.onerror = () => finish(null);
        v.src = url;
    } else {
        finish(null);
    }
});

/** Media background layer — mount as the first child of the banner surface. */
export const BannerMediaLayer: React.FC<{
    bgKey: string;
    /** Slide number + active slide let us keep exactly one <video> playing. */
    slideIndex?: number;
    activeSlide?: number;
    className?: string;
}> = ({ bgKey, slideIndex, activeSlide, className = '' }) => {
    const { url } = useBannerMedia(bgKey);
    if (!url) return null;

    const isActive = slideIndex === undefined || activeSlide === undefined || slideIndex === activeSlide;

    return (
        <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
            {isVideoUrl(url) ? (
                <video
                    src={url}
                    className="w-full h-full object-cover"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    autoPlay={isActive}
                    ref={(el) => { if (el) { if (isActive) el.play().catch(() => {}); else el.pause(); } }}
                />
            ) : (
                <img src={url} alt="" className="w-full h-full object-cover select-none" draggable={false} loading="lazy" />
            )}
        </div>
    );
};

/** Admin-only upload / remove buttons — mount inside a `relative` banner surface. */
export const BannerMediaControls: React.FC<{ bgKey: string; className?: string }> = ({ bgKey, className = '' }) => {
    const { url, isAdmin, busy, upload, remove } = useBannerMedia(bgKey);
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    if (!isAdmin) return null;

    const btn = 'w-6 h-6 rounded-full bg-black/65 border border-white/30 backdrop-blur-md flex items-center justify-center text-white/90 hover:text-white hover:bg-black/90 active:scale-90 transition-all cursor-pointer shadow-lg';

    return (
        <div className={`absolute z-30 flex items-center gap-1.5 ${className}`} onClick={(e) => e.stopPropagation()}>
            {url && (
                <button type="button" onClick={remove} className={btn} title={tc('remove')} aria-label={tc('remove')}>
                    {busy ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <X className="w-3 h-3" />}
                </button>
            )}
            <label className={btn} title={t('uploadAdminTitle')}>
                {busy ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload className="w-3 h-3" />}
                <input
                    type="file"
                    accept="image/*,video/*,.gif,.mp4,.webm,.mov,.heic,.heif,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) upload(file);
                    }}
                />
            </label>
        </div>
    );
};

export default BannerMediaLayer;